import axiosInstance from '../config/axios.js';

const API_BASE = '/dashboard';

/**
 * Get the current user's role from localStorage
 * @returns {string|null} User role or null if not available
 */
const getCurrentUserRole = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user?.role_name || null;
    }
  } catch (err) {
    console.error('[v0] Error parsing user data:', err);
  }
  return null;
};

/**
 * Get current user's ID
 * @returns {string|null} User ID or null if not available
 */
const getCurrentUserId = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      return user?.user_id || null;
    }
  } catch (err) {
    console.error('[v0] Error getting user ID:', err);
  }
  return null;
};

const dashboardService = {
  /**
   * Get dashboard data with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.from_date - Start date (YYYY-MM-DD)
   * @param {string} params.to_date - End date (YYYY-MM-DD)
   * @param {string} params.client_id - Filter by client (admin only)
   * @returns {Promise} Dashboard data
   */
  getDashboardData: async (params = {}) => {
    const userRole = getCurrentUserRole();
    const userId = getCurrentUserId();
    
    console.log('[v0] Dashboard data requested - Role:', userRole, '- User ID:', userId, '- Params:', params);
    
    const queryParams = {};
    
    if (params.from_date) {
      queryParams.from_date = params.from_date;
    }
    if (params.to_date) {
      queryParams.to_date = params.to_date;
    }
    if (params.client_id) {
      queryParams.client_id = params.client_id;
    }

    const response = await axiosInstance.get(API_BASE, { params: queryParams });
    
    // Log the role being used for this response
    console.log('[v0] Dashboard API response received - Role:', userRole, '- Records count:', Object.keys(response.data?.data || {}).length);
    
    return response.data;
  },

  /**
   * Get current user's role
   * @returns {string|null} User role
   */
  getCurrentUserRole,

  /**
   * Get current user's ID
   * @returns {string|null} User ID
   */
  getCurrentUserId,

  /**
   * Format date for API (YYYY-MM-DD)
   * @param {Date} date - Date object
   * @returns {string} Formatted date string
   */
  formatDate: (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  /**
   * Get date range presets
   * @returns {Object} Object with preset date ranges
   */
  getDatePresets: () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    
    const lastMonthStart = new Date(today);
    lastMonthStart.setMonth(lastMonthStart.getMonth() - 1);
    
    const lastThreeMonthsStart = new Date(today);
    lastThreeMonthsStart.setMonth(lastThreeMonthsStart.getMonth() - 3);
    
    const lastSixMonthsStart = new Date(today);
    lastSixMonthsStart.setMonth(lastSixMonthsStart.getMonth() - 6);
    
    const lastYearStart = new Date(today);
    lastYearStart.setFullYear(lastYearStart.getFullYear() - 1);
    
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const currentYearStart = new Date(today.getFullYear(), 0, 1);

    return {
      today: {
        from: dashboardService.formatDate(today),
        to: dashboardService.formatDate(today),
        label: 'Today'
      },
      yesterday: {
        from: dashboardService.formatDate(yesterday),
        to: dashboardService.formatDate(yesterday),
        label: 'Yesterday'
      },
      last7Days: {
        from: dashboardService.formatDate(lastWeekStart),
        to: dashboardService.formatDate(today),
        label: 'Last 7 Days'
      },
      last30Days: {
        from: dashboardService.formatDate(lastMonthStart),
        to: dashboardService.formatDate(today),
        label: 'Last 30 Days'
      },
      last3Months: {
        from: dashboardService.formatDate(lastThreeMonthsStart),
        to: dashboardService.formatDate(today),
        label: 'Last 3 Months'
      },
      last6Months: {
        from: dashboardService.formatDate(lastSixMonthsStart),
        to: dashboardService.formatDate(today),
        label: 'Last 6 Months'
      },
      lastYear: {
        from: dashboardService.formatDate(lastYearStart),
        to: dashboardService.formatDate(today),
        label: 'Last Year'
      },
      thisMonth: {
        from: dashboardService.formatDate(currentMonthStart),
        to: dashboardService.formatDate(today),
        label: 'This Month'
      },
      thisYear: {
        from: dashboardService.formatDate(currentYearStart),
        to: dashboardService.formatDate(today),
        label: 'This Year'
      }
    };
  },

  /**
   * Parse date string to display format
   * @param {string} dateStr - Date string (YYYY-MM-DD)
   * @returns {string} Formatted date for display
   */
  parseDisplayDate: (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }
};

export default dashboardService;
